import axios from 'axios';
import CheckBox from 'cds/forms/CheckBox';
import {
  forwardRef,
  useState,
  useCallback,
  ChangeEvent,
  useEffect,
  useRef,
  VFC,
  ReactNode,
} from 'react';
import styled, { css, keyframes } from 'styled-components';
import SpinLoading from 'components/atoms/common/SpinLoading';

import ReactDOM from 'react-dom';

const Section11 = forwardRef<HTMLElement>((_, ref) => {
  const name = useInput();
  const company = useInput();
  const email = useInput();
  const contact = useInput();
  const [companySize, setCompanySize] = useState('10명 이하');
  const [check, setCheck] = useState(false);
  const [content, setContent] = useState('');
  const onChangeContent = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
  }, []);
  const resetFormValues = useCallback(() => {
    name.setInput('');
    company.setInput('');
    email.setInput('');
    contact.setInput('');
    setCompanySize('10명 이하');
    setCheck(false);
    setContent('');
  }, [company, contact, email, name]);
  const { onToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const onCloseSuccessModal = useCallback(() => setIsSuccess(false), []);
  const onSubmit = useCallback(async () => {
    if (!name.input) {
      return onToast('성함을 입력해주세요');
    }
    if (!company.input) {
      return onToast('기업명을 입력해주세요');
    }
    if (!email.input) {
      return onToast('이메일 주소를 입력해주세요');
    }
    if (!contact.input) {
      return onToast('연락처를 입력해주세요');
    }
    if (!check) {
      return onToast('개인정보 수집/이용에 동의해주세요');
    }
    if (!content) {
      return onToast('문의 내용을 입력해주세요');
    }
    try {
      setIsLoading(true);
      const data = {
        성함: name.input,
        기업명: company.input,
        이메일주소: email.input,
        연락처: contact.input,
        기업인원: companySize,
        문의내용: content,
      };
      await axios.post('/new/api/biz/slack', data);
      setIsSuccess(true);
      resetFormValues();
    } catch (error) {
      onToast('오류가 발생하였습니다.\n다시 시도해주세요.');
      if (process.env.NODE_ENV !== 'production') {
        console.error(error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    name.input,
    company.input,
    email.input,
    contact.input,
    check,
    content,
    onToast,
    companySize,
    resetFormValues,
  ]);

  return (
    <section ref={ref}>
      <GlobalLoading isLoading={isLoading} />
      <SuccessModal isSuccess={isSuccess} onCloseSuccessModal={onCloseSuccessModal} />
      <L_Section7Form>
        <L_Section7FormInputContainer>
          <L_Section7FormInputLabel>성함</L_Section7FormInputLabel>
          <L_Section7FormInput value={name.input} onChange={name.onChangeInput} />
        </L_Section7FormInputContainer>
        <L_Section7FormInputContainer>
          <L_Section7FormInputLabel>기업명</L_Section7FormInputLabel>
          <L_Section7FormInput value={company.input} onChange={company.onChangeInput} />
        </L_Section7FormInputContainer>
        <L_Section7FormInputContainer>
          <L_Section7FormInputLabel>이메일 주소</L_Section7FormInputLabel>
          <L_Section7FormInput value={email.input} onChange={email.onChangeInput} />
        </L_Section7FormInputContainer>
        <L_Section7FormInputContainer>
          <L_Section7FormInputLabel>연락처</L_Section7FormInputLabel>
          <L_Section7FormInput value={contact.input} onChange={contact.onChangeInput} />
        </L_Section7FormInputContainer>
        <L_Section7FormInputContainer>
          <L_Section7FormInputLabel>기업인원</L_Section7FormInputLabel>
          <L_Section7FormSelect
            value={companySize}
            onChange={e => {
              setCompanySize(e.target.value);
            }}
          >
            <option>10명 이하</option>
            <option>50명 이하</option>
            <option>100명 이하</option>
            <option>300명 이하</option>
            <option>500명 이하</option>
            <option>500명 이상</option>
          </L_Section7FormSelect>
        </L_Section7FormInputContainer>
        <L_Section7FormInputContainer>
          <L_Section7FormInputLabel>문의 내용</L_Section7FormInputLabel>
          <L_Section7FormTextarea value={content} onChange={onChangeContent} />
        </L_Section7FormInputContainer>
        <CheckBoxContainer>
          <CheckBox
            checked={check}
            // @ts-ignore
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setCheck(e.target.checked);
            }}
          />
          <div>
            <span>[필수] </span>
            <a
              target="_blank"
              rel="noreferrer noopener"
              href="https://www.classu.co.kr/new/customer/agreement"
            >
              개인정보 수집/ 이용
            </a>
            <span>에 동의합니다.</span>
          </div>
        </CheckBoxContainer>

        <ButtonContainer>
          <button onClick={onSubmit}>문의하기</button>
        </ButtonContainer>
      </L_Section7Form>
    </section>
  );
});

const ButtonContainer = styled.div`
  width: max-content;
  margin-left: auto;
  background-color: #eb5149;
  border-radius: 0.5rem;
  & > button {
    padding: 0.5rem 2rem;
    font-weight: 600;
    font-size: 1.6rem;
    color: #fff;
  }
`;

const CheckBoxContainer = styled.div`
  width: 100%;
  text-align: left;
  margin-top: 3rem;
  span {
    color: ${palette.grey25};
  }
  & > label > span {
    top: 0.3rem !important;
  }
  & > div {
    margin-top: 1rem;
  }
  & > div > span {
    font-size: 1.6rem;
    font-weight: 300;
  }
  & > div > span:nth-of-type(1) {
    margin-left: 3.2rem;
    font-size: 1.6rem;
    font-weight: 300;
  }
  & > div > a {
    font-size: 1.6rem;
    font-weight: 300;
    text-decoration: underline;
    color: ${palette.grey25};
  }

  & > label > span {
    background-color: ${palette.BTN.BG};
    border: 1px solid ${palette.grey0};
  }
`;

const L_Section7Form = styled.div`
  margin: 0 auto;
  max-width: 437px;
  width: calc(100% - 3rem);
  padding-bottom: 12rem;
  * {
    text-align: left;
  }
`;
Section11.displayName = 'form';

export default Section11;

const useInput = () => {
  const [input, setInput] = useState('');
  const onChangeInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  }, []);
  return { input, onChangeInput, setInput };
};
const L_Section7FormTextarea = styled.textarea`
  width: 100%;
  resize: none;
  height: 55rem;
  padding: 1.6rem;
  outline: none;
  height: 30rem;
  border: 1px solid ${palette.system.Null};
  font-weight: 300;
  font-size: 1.6rem;
  padding: 0.5rem 1rem;
  background-color: ${palette.BTN.BG};
  color: ${palette.grey0};
`;
const L_Section7FormSelect = styled.select`
  width: 100%;
  padding: 0.4rem;
  font-size: 1.6rem;
  outline: none;
  border: 1px solid ${palette.system.Null};
  background-color: ${palette.BTN.BG};
  color: ${palette.grey0};
`;
const L_Section7FormInputLabel = styled.div`
  text-align: left;
  font-weight: 300;
  font-size: 1.6rem;
  margin-bottom: 1rem;
  color: ${palette.grey25};
`;

const L_Section7FormInput = styled.input`
  width: 100%;
  font-weight: 300;
  font-size: 1.6rem;
  padding: 0.5rem 1rem;
  border: 1px solid ${palette.system.Null};
  outline: none;
  background-color: ${palette.BTN.BG};
  color: ${palette.grey0};
`;
const L_Section7FormInputContainer = styled.div`
  position: relative;
  width: 100%;
  text-align: left;
  margin-top: 3rem;
`;

const useToast = () => {
  const portalEle = useRef<HTMLElement | null>(null);
  const toastContainer = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const isDefined = customElements.get('classu-portal');
    if (!isDefined) {
      class ClassuPortalClass extends HTMLElement {
        // eslint-disable-next-line no-useless-constructor
        constructor() {
          super();
        }
      }
      customElements.define('classu-portal', ClassuPortalClass);
    }
  }, []);

  // return createPortal(children, portalEle.current);

  const onToast = useCallback((str: string) => {
    if (typeof window === 'undefined') {
      return;
    }
    if (!portalEle.current || !document.getElementById('toast-container')) {
      const portalContainer = document.createElement('classu-portal');
      portalContainer.style.position = 'fixed';
      portalContainer.style.top = '10%';
      portalContainer.style.left = '50%';
      portalContainer.style.transform = 'translateX(-50%)';
      portalEle.current = portalContainer;
      document.body.appendChild(portalEle.current);
    }
    if (!toastContainer.current || !document.getElementById('toast-container')) {
      toastContainer.current = document.createElement('div');
      toastContainer.current.id = 'toast-container';
      toastContainer.current.style.display = 'flex';
      toastContainer.current.style.flexDirection = 'column-reverse';
      toastContainer.current.style.rowGap = '8px';
      portalEle.current.appendChild(toastContainer.current);
    }
    const curToastContainer = document.createElement('div');
    toastContainer.current.appendChild(curToastContainer);
    if (toastContainer.current.childNodes.length > 10) {
      toastContainer.current.removeChild(toastContainer.current.firstElementChild);
    }
    ReactDOM.render(<Toast text={str} />, curToastContainer);
    setTimeout(() => {
      console.log(toastContainer.current.firstChild);
      if (toastContainer.current.childNodes.length === 1) {
        portalEle.current.remove();
      } else {
        toastContainer.current.removeChild(toastContainer.current.firstElementChild);
      }
    }, 2500);
  }, []);
  return { onToast };
};
const Toast: VFC<{ text: string }> = ({ text }) => {
  const [mount, setMount] = useState(false);
  const [exit, setExit] = useState(false);
  useEffect(() => {
    setMount(true);
    setTimeout(() => {
      setMount(false);
      setExit(true);
    }, 2000);
  }, []);
  return (
    <ToastContainer exit={exit} isMount={mount}>
      {text}
    </ToastContainer>
  );
};
const ToastContainer = styled.div<{ isMount: boolean; exit: boolean }>`
  transition: all 0.8s;
  opacity: ${({ isMount }) => (isMount ? 1 : 0)};
  max-height: ${({ isMount }) => (isMount ? '100vh' : '0px')};
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateX(
    ${({ isMount, exit }) => (!exit && isMount ? '0' : exit ? '10vw' : '-10vw')}
  );
  background-color: #fff;
  border-radius: 16px;
  padding: 2rem 4rem;
  font-size: 1.6rem;
`;

import { createPortal } from 'react-dom';
import { onlyHover, palette } from 'cds/styles';
import Icon from 'cds/icons';

const ClassuPortal = ({ children }: { children: ReactNode }) => {
  const [mounted, setMounted] = useState(false);
  const portalEle = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const isDefined = customElements.get('classu-portal');
    if (!isDefined) {
      class ClassuPortalClass extends HTMLElement {
        // eslint-disable-next-line no-useless-constructor
        constructor() {
          super();
        }
      }
      customElements.define('classu-portal', ClassuPortalClass);
    }
    setMounted(true);
    portalEle.current = document.createElement('classu-portal');
    document.body.appendChild(portalEle.current);
    return () => {
      if (portalEle.current != null) {
        document.body.removeChild(portalEle.current);
      }
    };
  }, []);

  if (!mounted || portalEle.current == null) {
    return null;
  }

  return createPortal(children, portalEle.current);
};

const DURATION = 500 as const;

const SuccessModal: VFC<{ isSuccess: boolean; onCloseSuccessModal: () => void }> = ({
  isSuccess,
  onCloseSuccessModal,
}) => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (isSuccess) {
      return setOpen(true);
    }
    setTimeout(() => {
      setOpen(false);
    }, DURATION - 50);
  }, [isSuccess]);

  if (!open) {
    return null;
  }
  return (
    <ClassuPortal>
      <L_GlobalLoading
        onClick={onCloseSuccessModal}
        open={open && isSuccess}
        backgroundOpacity={0.3}
      >
        <div
          onClick={e => {
            e.stopPropagation();
          }}
          style={{
            backgroundColor: '#FFF',
            padding: '4rem',
            borderRadius: '1rem',
            position: 'relative',
          }}
        >
          <div style={{ position: 'absolute', right: 0, top: 0 }}>
            <CloseButton onClick={onCloseSuccessModal}>
              {/* @ts-ignore */}
              <Icon name="ic_close_m" width="16" height="16" fill={palette.black} />
            </CloseButton>
          </div>
          <div style={{ fontSize: '2rem', textAlign: 'center' }}>
            <span>문의가 정상적으로 접수되었습니다</span>
            <br />
            <span>클래스유에서 곧 연락드리겠습니다</span>
          </div>
        </div>
      </L_GlobalLoading>
    </ClassuPortal>
  );
};

const CloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  opacity: 0.4;
  cursor: pointer;

  ${onlyHover} {
    &:hover {
      opacity: 1;
    }
  }

  &:active {
    opacity: 1;
  }
`;

const GlobalLoading = ({ isLoading }: { isLoading: boolean }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (isLoading) {
      return setOpen(true);
    }
    setTimeout(() => {
      setOpen(false);
    }, DURATION - 50);
  }, [isLoading]);

  if (!open) {
    return null;
  }
  return (
    <ClassuPortal>
      <L_GlobalLoading open={open && isLoading}>
        {/* @ts-ignore */}
        <SpinLoading />
      </L_GlobalLoading>
    </ClassuPortal>
  );
};

const fadeIn = keyframes`
    0% { opacity:0 }
    100% { opacity:1 }
`;
const fadeOut = keyframes`
    0% { opacity:1 }
    100% { opacity:0 }
`;
const L_GlobalLoading = styled.section<{ open: boolean; backgroundOpacity?: number }>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, ${({ backgroundOpacity }) => backgroundOpacity ?? 0.7});
  z-index: 200;
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ open }) =>
    open
      ? css`
          animation: ${fadeIn} ${DURATION}ms;
        `
      : css`
          animation: ${fadeOut} ${DURATION}ms;
        `};
`;
